// Header — responsive site header: ZJL logo, primary nav (6 pages), language
// switcher, and a "work with us" CTA. Collapses to a toggle-able menu on small
// screens. Nav labels come from the `common` namespace.
//
// Logo: references /logo.svg (Agent D supplies the real emblem in public/). If the
// image fails to load, a tasteful text fallback "佐江龙 ZJL" is shown instead.
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type AppLocale, DEFAULT_LOCALE } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import MobileMenu from "./MobileMenu";

function isLocale(v: string | undefined): v is AppLocale {
  return !!v && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

/** Nav segments in order. "" is the locale home (index route). */
const NAV_SEGMENTS = ["", "about", "services", "network", "partners", "contact"] as const;

export default function Header() {
  const { lang } = useParams();
  const location = useLocation();
  const { t } = useTranslation("common");

  const current: AppLocale = isLocale(lang) ? lang : DEFAULT_LOCALE;

  const [menuOpen, setMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Show the Latin mark only when it differs from the short name — otherwise the
  // brand reads "ZJL ZJL" in en (where shortName and latin are both "ZJL").
  const showLatin =
    t("brand.latin").trim().toLowerCase() !== t("brand.shortName").trim().toLowerCase();

  // The hamburger toggle, so focus can be restored to it when the menu closes
  // (a11y: closing the sheet shouldn't drop keyboard focus to the document body).
  const toggleRef = useRef<HTMLButtonElement>(null);
  // Skip the focus-restore on the very first render (menu starts closed).
  const wasOpenRef = useRef(false);

  // Close the mobile menu on any route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // When the menu goes from open -> closed, return focus to the toggle button.
  useEffect(() => {
    if (wasOpenRef.current && !menuOpen) {
      toggleRef.current?.focus();
    }
    wasOpenRef.current = menuOpen;
  }, [menuOpen]);

  // Add a subtle border/shadow once the page is scrolled past the top.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const home = `/${current}`;

  return (
    <header
      className={`sticky top-0 z-50 bg-white/85 backdrop-blur-md transition-shadow ${
        scrolled
          ? "border-b border-[var(--brand-tint)] shadow-[var(--shadow-card)]"
          : "border-b border-transparent"
      }`}
    >
      <a
        href="#main-content"
        className="sr-only rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-4 py-2 font-medium text-white focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[var(--brand-azure)]"
      >
        {t("header.skipToContent")}
      </a>

      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-4 px-6">
        <Link
          to={home}
          className="flex items-center gap-2.5 rounded-[var(--radius-md)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
          aria-label={t("brand.name")}
        >
          {logoFailed ? (
            <span
              className="inline-flex h-9 items-center gap-1.5 font-zh text-[var(--text-xl)] font-bold leading-none text-[var(--brand-azure)]"
              aria-hidden="true"
            >
              <span>{t("brand.shortName")}</span>
              {showLatin && (
                <span className="font-sans text-[var(--text-base)] font-semibold tracking-wide text-[var(--brand-primary)]">
                  {t("brand.latin")}
                </span>
              )}
            </span>
          ) : (
            <img
              src="/logo.svg"
              alt=""
              className="h-9 w-9 shrink-0"
              width={36}
              height={36}
              onError={() => setLogoFailed(true)}
            />
          )}
          <span className="flex items-baseline gap-1.5">
            <span className="font-zh text-[var(--text-xl)] font-bold leading-none text-[var(--ink)]">
              {t("brand.shortName")}
            </span>
            {showLatin && (
              <span className="font-sans text-[var(--text-base)] font-semibold tracking-wide text-[var(--brand-primary)]">
                {t("brand.latin")}
              </span>
            )}
          </span>
        </Link>

        {/* Desktop primary nav (md+). */}
        <nav
          className="hidden items-center gap-1 md:flex"
          aria-label={t("header.menu")}
        >
          {NAV_SEGMENTS.map((seg) => (
            <NavLink
              key={seg || "home"}
              to={`/${current}${seg ? `/${seg}` : ""}`}
              end={seg === ""}
              className={({ isActive }) =>
                `relative px-3 py-2 text-[var(--text-base)] font-medium transition-colors after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-[var(--brand-primary)] after:transition-transform after:duration-200 ${
                  isActive
                    ? "text-[var(--brand-azure-text)] after:scale-x-100"
                    : "text-[var(--ink)] hover:text-[var(--brand-azure-text)] after:scale-x-0 hover:after:scale-x-100"
                }`
              }
            >
              {t(`nav.${seg || "home"}`)}
            </NavLink>
          ))}
        </nav>

        {/* Desktop actions (md+). */}
        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher onSwitch={() => setMenuOpen(false)} />
          <Link
            to={`/${current}/contact`}
            className="inline-flex items-center rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-5 py-2.5 text-[var(--text-base)] font-medium text-white shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--brand-azure)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
          >
            {t("header.cta")}
          </Link>
        </div>

        {/* Mobile hamburger toggle (<md). */}
        <button
          ref={toggleRef}
          type="button"
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] text-[var(--ink)] transition-colors hover:bg-[var(--brand-tint-3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)] md:hidden"
          aria-expanded={menuOpen}
          aria-controls="primary-nav"
          aria-label={menuOpen ? t("header.closeMenu") : t("header.openMenu")}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="sr-only">
            {menuOpen ? t("header.closeMenu") : t("header.openMenu")}
          </span>
          <span aria-hidden="true" className="relative block h-4 w-5">
            <span
              className={`absolute left-0 block h-0.5 w-5 rounded-full bg-current transition-all duration-200 ${
                menuOpen ? "top-[7px] rotate-45" : "top-0"
              }`}
            />
            <span
              className={`absolute left-0 top-[7px] block h-0.5 w-5 rounded-full bg-current transition-opacity duration-200 ${
                menuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 block h-0.5 w-5 rounded-full bg-current transition-all duration-200 ${
                menuOpen ? "top-[7px] -rotate-45" : "top-[14px]"
              }`}
            />
          </span>
        </button>
      </div>

      <MobileMenu
        id="primary-nav"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        current={current}
        segments={NAV_SEGMENTS}
        t={t}
      />
    </header>
  );
}
